import type { DateTime } from "luxon";
import type { NurseInShift } from "../../../../utils/models";

/** A nurse's next high-cost shift starting within this long of the previous one's end continues the same stretch of work. */
export const CONSECUTIVE_SHIFT_MAX_GAP_MINUTES = 60;

/**
 * Groups each nurse's consecutive high-cost shifts into one run, so a 7a–3p + 3p–11p double reads as a
 * single row. A shift joins the current run when it starts no more than CONSECUTIVE_SHIFT_MAX_GAP_MINUTES
 * after the run's latest end. Each run is chronological; runs keep the order their shifts had in `shifts`.
 */
export function mergeConsecutiveShifts(shifts: NurseInShift[]): NurseInShift[][] {
	const byNurse = new Map<number, NurseInShift[]>();
	for (const shift of shifts) {
		const nurseShifts = byNurse.get(shift.nurseId);
		if (nurseShifts) nurseShifts.push(shift);
		else byNurse.set(shift.nurseId, [shift]);
	}

	const runOf = new Map<NurseInShift, NurseInShift[]>();
	for (const nurseShifts of byNurse.values()) {
		const sorted = [...nurseShifts].sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
		let run: NurseInShift[] = [];
		let runEnd: DateTime = sorted[0].endTime;
		for (const shift of sorted) {
			if (run.length > 0 && shift.startTime.diff(runEnd, "minutes").minutes > CONSECUTIVE_SHIFT_MAX_GAP_MINUTES) {
				run = [];
			}
			if (run.length === 0 || shift.endTime > runEnd) runEnd = shift.endTime;
			run.push(shift);
			runOf.set(shift, run);
		}
	}

	// Emit each run when its first-listed shift comes up, so runs keep the input order in one pass.
	const runs: NurseInShift[][] = [];
	const emitted = new Set<NurseInShift[]>();
	for (const shift of shifts) {
		const run = runOf.get(shift);
		if (run && !emitted.has(run)) {
			emitted.add(run);
			runs.push(run);
		}
	}
	return runs;
}
